import { useCallback } from "react";
import { Separator, useModal, type ModalOptions } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { CustomerCardContent } from "../components/customer-card.content";
import { CardHeading } from "../components/card-heading";

interface UseCustomerCardModalOptions {
  providerId?: number;
  onSaved?: () => void;
}

/** Shell geometry shared by all three entry points, so they can't diverge. */
const CARD_MODAL_SHELL = {
  size: "custom",
  width: { md: 1000 },
  height: { md: 700 },
  closeButtonTop: "top-4",
} satisfies Partial<ModalOptions>;

const CARD_BODY_CLASS = "!p-0 flex flex-col flex-1 min-h-0 overflow-hidden";

// The openers are memoized: a table that lists them among a memoized column
// array's dependencies would otherwise rebuild its columns on every render.
export function useCustomerCardModal(options?: UseCustomerCardModalOptions) {
  const { openModal } = useModal();
  const t = useT();
  const providerId = options?.providerId;
  const onSaved = options?.onSaved;

  const openNavigation = useCallback(
    (rows: ConnectProto.Organization[], initialIndex: number) => {
      if (rows.length === 0) return;
      const clampedInitialIndex = Math.min(rows.length - 1, Math.max(0, initialIndex));
      openModal({
        ...CARD_MODAL_SHELL,
        navigation: {
          items: rows,
          initialIndex: clampedInitialIndex,
          getItemId: (item) => item.id.toString(),
          renderTitle: (item) => <CardHeading name={item.name} />,
          renderBody: (item) => (
            <>
              <Separator />
              <CustomerCardContent initial={item} providerId={providerId} onSaved={onSaved} />
            </>
          ),
        },
      });
    },
    [openModal, providerId, onSaved],
  );

  const openNew = useCallback(() => {
    openModal({
      ...CARD_MODAL_SHELL,
      title: (
        <span className="text-base font-bold leading-tight text-text-primary">
          {t(Namespace.Customers, "card.title")}
        </span>
      ),
      bodyClassName: CARD_BODY_CLASS,
      body: <CustomerCardContent providerId={providerId} onSaved={onSaved} />,
    });
  }, [openModal, t, providerId, onSaved]);

  const openSingle = useCallback(
    (item: ConnectProto.Organization) => {
      openModal({
        ...CARD_MODAL_SHELL,
        title: <CardHeading name={item.name} />,
        bodyClassName: CARD_BODY_CLASS,
        body: <CustomerCardContent initial={item} providerId={providerId} onSaved={onSaved} />,
      });
    },
    [openModal, providerId, onSaved],
  );

  return { openNavigation, openNew, openSingle };
}
